import React, {useEffect, useState} from 'react'
import {Box, Divider, Typography} from '@mui/material'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined'
import dayjs from 'dayjs'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import apiClient from 'src/services/apiClient'
import {
  getBodyPartSuggestions,
  SECONDARY_ASSUMPTION_STATES,
  SecondaryAssumption
} from 'src/constants/secondaryAssumptions'
import BodyPartPickerNew from './BodyPartPickerNew'
import SectionTitleNew from './SectionTitleNew'

// The dialog of the "Prossima assunzione" card (the derived next dose, RF3.1.1), opened by its ✓
// or ✗. One dialog, two independent actions, each its own footer button and its own call:
// - "Conferma assunzione"/"Conferma non assunta" answers the dose ahead of time (RF4.3, by
//   natural key) — the primary action;
// - "Riprogramma" moves it to another time without answering it (RF4.5).
// They are never sent together: they concern the same dose, and "taken" and "moved" contradict
// each other. Each button only validates and sends its own section.

interface NextDoseDialogNewProps {
  open: boolean
  dose?: SecondaryAssumption
  // which card button opened the dialog
  presetTaken: 'yes' | 'no'
  onClose: () => void
  onAnswered: (updated: SecondaryAssumption) => void
  onMoved: () => void
}

const sameMinute = (a?: string | null, b?: string | null): boolean =>
  Boolean(a && b) && dayjs(a).startOf('minute').isSame(dayjs(b).startOf('minute'))

export default function NextDoseDialogNew({
  open,
  dose,
  presetTaken,
  onClose,
  onAnswered,
  onMoved
}: NextDoseDialogNewProps): JSX.Element {
  const toast = useToast()
  const configuredBodyParts = useAppSelector(
    state => state.pspConfigReducer.data.therapyDiaryConfigurations?.secondaryTherapiesConfiguration?.bodyParts
  )
  const bodyPartSuggestions = getBodyPartSuggestions(configuredBodyParts)

  const [takenAt, setTakenAt] = useState<string | null>(null)
  const [bodyPart, setBodyPart] = useState<string[]>([])
  const [movedTo, setMovedTo] = useState<string | null>(null)
  const [answerError, setAnswerError] = useState<string>()
  const [moveError, setMoveError] = useState<string>()
  const [statusAnswer, setStatusAnswer] = useState<string>()
  const [statusMove, setStatusMove] = useState<string>()

  // Reset on every open. Both sections start from the planned time; the time picker's "Adesso"
  // sets "when did you take it" to the current time in one tap.
  useEffect(() => {
    if (!open || !dose) { return }
    const expected = dose.expectedDate ?? null
    setTakenAt(expected)
    setBodyPart([])
    setMovedTo(expected)
    setAnswerError(undefined)
    setMoveError(undefined)
    setStatusAnswer(undefined)
    setStatusMove(undefined)
  }, [open, dose])

  const isPending = statusAnswer === STATUS.PENDING || statusMove === STATUS.PENDING
  const isMoveEdited = !sameMinute(movedTo, dose?.expectedDate)

  const validateMove = (): string | undefined => {
    if (!movedTo || !dayjs(movedTo).isValid()) { return 'Indica data e ora della nuova assunzione' }
    if (!isMoveEdited) { return 'Nessuna modifica alla data e all\'ora di questa assunzione' }
    if (!dayjs(movedTo).isAfter(dayjs())) { return 'L\'assunzione non può essere spostata nel passato' }
    return undefined
  }

  const validateAnswer = (): string | undefined => {
    if (presetTaken === 'no') { return undefined }
    if (!takenAt || !dayjs(takenAt).isValid()) { return 'Indica quando l\'hai assunta' }
    return undefined
  }

  const onMove = async (): Promise<void> => {
    if (!dose) { return }
    const error = validateMove()
    setMoveError(error)
    if (error) { return }
    try {
      const result = await apiClient.moveSecondaryNextDose({
        secondaryTherapyId: dose.secondaryTherapyId,
        expectedDate: dose.expectedDate as string,
        nextExpectedDate: dayjs(movedTo).startOf('minute').toISOString()
      }, setStatusMove)
      if (!result) { throw new Error('move failed') }
      toast.success('Assunzione riprogrammata')
      onMoved()
    } catch {
      // RF4.5 answers 400 when the card is stale (the cursor moved meanwhile): reload it
      toast.error('L\'assunzione è cambiata nel frattempo: il diario è stato aggiornato, riprova.')
      onMoved()
    }
  }

  const onAnswer = async (): Promise<void> => {
    if (!dose) { return }
    const error = validateAnswer()
    setAnswerError(error)
    if (error) { return }
    const bodyParts = Array.from(new Set(bodyPart.map(part => part.trim()).filter(Boolean)))
    const body = presetTaken === 'no'
      ? {state: SECONDARY_ASSUMPTION_STATES.CANCELLED}
      : {
          state: SECONDARY_ASSUMPTION_STATES.COMPLETED,
          takenAt: dayjs(takenAt).startOf('minute').toISOString(),
          ...bodyParts.length > 0 ? {bodyPart: bodyParts} : {}
        }
    try {
      const updated = await apiClient.patchSecondaryAssumptionByKey(
        {secondaryTherapyId: dose.secondaryTherapyId, expectedDate: dose.expectedDate as string},
        body,
        setStatusAnswer
      )
      if (!updated) { throw new Error('answer failed') }
      toast.success(presetTaken === 'no' ? 'Segnata come non assunta' : 'Risposta registrata')
      onAnswered(updated)
    } catch {
      toast.error('Impossibile registrare la risposta. Riprova.')
    }
  }

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={dose?.drugName ?? 'Terapia accessoria'}
      maxWidth="sm"
      actions={(
        <>
          <Button variant="secondary" loading={statusMove === STATUS.PENDING} disabled={isPending} onClick={onMove}>
            Riprogramma
          </Button>
          <Button variant="primary" loading={statusAnswer === STATUS.PENDING} disabled={isPending} onClick={onAnswer}>
            {presetTaken === 'no' ? 'Conferma non assunta' : 'Conferma assunzione'}
          </Button>
        </>
      )}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        {presetTaken === 'yes' ? (
          <Box>
            <SectionTitleNew icon={EventAvailableOutlinedIcon}>Conferma assunzione</SectionTitleNew>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
              <DatePickerNew
                label="Data"
                required
                fullWidth
                disabled={isPending}
                error={Boolean(answerError)}
                value={takenAt}
                onChange={(value) => { setTakenAt(value); setAnswerError(undefined) }}
              />
              <TimePickerNew
                label="Ora"
                required
                fullWidth
                disabled={isPending}
                error={Boolean(answerError)}
                showNowAction
                value={takenAt}
                onChange={(value) => { setTakenAt(value); setAnswerError(undefined) }}
              />
            </Box>
            {answerError && (
              <Typography variant="caption" sx={{color: 'error.main', mt: 1, display: 'block'}}>{answerError}</Typography>
            )}
            <Box sx={{mt: 3}}>
              <BodyPartPickerNew value={bodyPart} onChange={setBodyPart} suggestions={bodyPartSuggestions} disabled={isPending} />
            </Box>
          </Box>
        ) : (
          <Box>
            <SectionTitleNew icon={EventAvailableOutlinedIcon}>Conferma non assunta</SectionTitleNew>
            <Typography variant="body2" sx={{color: NEUTRAL_SCALE[700]}}>
              Confermi di non aver assunto questa dose? Se invece vuoi solo cambiarne l'orario, usa "Riprogramma".
            </Typography>
          </Box>
        )}

        <Divider />

        <Box>
          <SectionTitleNew icon={UpdateOutlinedIcon}>Riprogramma questa assunzione</SectionTitleNew>
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
            <DatePickerNew
              label="Data"
              required
              fullWidth
              disabled={isPending}
              error={Boolean(moveError)}
              minDate={new Date().toISOString()}
              value={movedTo}
              onChange={(value) => { setMovedTo(value); setMoveError(undefined) }}
            />
            <TimePickerNew
              label="Ora"
              required
              fullWidth
              disabled={isPending}
              error={Boolean(moveError)}
              value={movedTo}
              onChange={(value) => { setMovedTo(value); setMoveError(undefined) }}
            />
          </Box>
          {moveError && (
            <Typography variant="caption" sx={{color: 'error.main', mt: 1, display: 'block'}}>{moveError}</Typography>
          )}
          <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500], mt: 1, display: 'block'}}>
            Puoi spostare solo questa assunzione: le successive restano all'orario abituale.
          </Typography>
        </Box>
      </Box>
    </AppDialog>
  )
}
